import React from 'react';
import { BadgeCheckGreenIcon, CloseRedIcon, CloseTransparentIcon, QuestionBlueIcon } from '@assets/icons/index';
import {
    Button,
    Divider,
    Flex,
    List,
    ListIcon,
    ListItem,
    Modal,
    ModalContent,
    ModalOverlay,
    Text,
    VStack,
} from '@chakra-ui/react';
import { ConditionalComponent } from '@components/functionComponent';
import { IconCopy } from '@pages/UploadSellout/assets/icons';
import { onDownloadFileError } from '@pages/UploadSellout/helpers/crud';
import { useDownloadNotification } from '@store/downloadNotification';
import { usePopUpNotification } from '@store/popupNotification';
import { useSnackbarNotification } from '@store/snackbarNotification';
import copy from 'copy-to-clipboard';
import { getDetailLineErrorMessage } from './helpers';
import './styles.scss';
import { PDFIcon, ExcelIcon } from '@assets/icons';

const PopUp = () => {
    const { onChangeSnackbarNotification } = useSnackbarNotification();
    const { onChangeDownloadNotification } = useDownloadNotification();
    const { onResetPopUpNotification, status, errorMessages, additionalInfo } = usePopUpNotification();
    const lineError = getDetailLineErrorMessage(errorMessages);
    const onClose = onResetPopUpNotification;
    const isOpen = !!status;
    const Icon = additionalInfo?.selectedFile?.name.includes('.pdf') ? PDFIcon : ExcelIcon;

    const copyToClipboard = () => {
        copy(
            `nama toko: ${additionalInfo?.storeName},\r\nemail: ${additionalInfo?.email},\r\ntype upload: ${
                additionalInfo?.typeUpload
            },\r\ndate upload: ${additionalInfo?.dateUpload},\r\nerror status: ${
                additionalInfo?.errorStatus
            },\r\nerror message: ${
                Array.isArray(lineError) ? lineError.map((err) => err.description).join(',\n') : errorMessages
            }`,
        );
        onChangeSnackbarNotification({
            status: 'success',
            title: 'Berhasil',
            description: `Text berhasil di copy ke clipboard`,
            duration: 3,
        });
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered>
            <ModalOverlay />
            <ModalContent
                display="flex"
                gap="16px"
                minWidth="520px"
                borderRadius="12px"
                padding="24px"
                position="relative"
                maxHeight="90svh"
                overflowY="auto"
            >
                <Button
                    variant="unstyled"
                    position="absolute"
                    paddingX={0}
                    minWidth={0}
                    width="fit-content"
                    height="fit-content"
                    top="16px"
                    right="16px"
                    onClick={onClose}
                >
                    <CloseTransparentIcon width="24px" height="auto" />
                </Button>

                <Flex justify="center" paddingTop="12px">
                    <ConditionalComponent
                        statement={additionalInfo?.errorStatus === 200 || additionalInfo?.errorStatus === 201}
                        fallback={<CloseRedIcon height={14} width={14} />}
                    >
                        <BadgeCheckGreenIcon height={14} width={14} />
                    </ConditionalComponent>
                </Flex>

                <Flex flexDirection="column" alignItems="center" gap="8px" borderRadius="8px" textAlign="center">
                    <Flex
                        backgroundColor="semantic.200"
                        alignItems="center"
                        justifyContent="center"
                        gap="8px"
                        py="10px"
                        px="12px"
                        borderRadius="full"
                    >
                        <Icon size={16} />
                        <Text fontSize="12px" fontWeight="600">
                            {additionalInfo?.uploadedFile}
                        </Text>
                    </Flex>
                    <ConditionalComponent
                        statement={
                            additionalInfo?.errorStatus === 200 ||
                            additionalInfo?.errorStatus === 201 ||
                            status === 'success'
                        }
                        fallback={
                            <Text color="danger.500" fontSize="24px" fontWeight="bold">
                                File Gagal di Upload
                            </Text>
                        }
                    >
                        <Flex flexDirection="column" alignItems="center">
                            <Text color="success.600" fontSize="24px" fontWeight="bold">
                                File Sukses di Upload
                            </Text>
                            <ConditionalComponent statement={errorMessages}>
                                <Text>Tetapi ada catatan yang perlu diperhatikan di bawah ini.</Text>
                            </ConditionalComponent>
                        </Flex>
                    </ConditionalComponent>
                </Flex>

                <Flex flexDirection="column" gap="16px" bg="gray.50" borderRadius="8px" p="16px">
                    <ConditionalComponent statement={lineError || errorMessages}>
                        <Flex justify="space-between" align="center">
                            <Text fontSize="16px" fontWeight="bold" color="gray.700">
                                Detail Kesalahan
                            </Text>
                            <Button
                                variant="unstyled"
                                paddingX={0}
                                minWidth={0}
                                height="fit-content"
                                onClick={copyToClipboard}
                                color="sagitarius.500"
                            >
                                <IconCopy />
                            </Button>
                        </Flex>

                        <Flex
                            flexDirection="column"
                            bg="danger.100"
                            padding="0px 12px 12px 12px"
                            borderRadius="8px"
                            maxHeight="200px"
                            overflowY="auto"
                            position="relative"
                        >
                            <Text
                                color="danger.600"
                                fontWeight="700"
                                fontSize="14px"
                                paddingTop="12px"
                                paddingBottom="8px"
                                position="sticky"
                                top={0}
                                zIndex={1}
                                backgroundColor="danger.100"
                            >
                                {Array.isArray(lineError) ? lineError.length : 1} Kesalahan File Ditemukan:
                            </Text>
                            <List spacing="10px" paddingLeft="12px">
                                {Array.isArray(lineError) ? (
                                    lineError.map((err, idx) => (
                                        <ListItem
                                            key={`err-${idx}`}
                                            display="flex"
                                            alignItems="center"
                                            gap="8px"
                                            fontSize="14px"
                                        >
                                            <ListIcon as={CloseRedIcon} />
                                            {err.description || err}
                                        </ListItem>
                                    ))
                                ) : (
                                    <ListItem display="flex" alignItems="center" gap="8px" fontSize="14px">
                                        <ListIcon as={CloseRedIcon} />
                                        {lineError}
                                    </ListItem>
                                )}
                            </List>
                        </Flex>
                        <Divider orientation="horizontal" />
                    </ConditionalComponent>

                    <VStack align="stretch" spacing="12px">
                        <Flex justify="space-between">
                            <Text fontSize="14px" color="neutral.500">
                                Nama Toko
                            </Text>
                            <Text fontSize="14px" color="semantic.700">
                                {additionalInfo?.storeName}
                            </Text>
                        </Flex>
                        <Flex justify="space-between">
                            <Text fontSize="14px" color="neutral.500">
                                Email
                            </Text>
                            <Text fontSize="14px" color="semantic.700">
                                {additionalInfo?.email}
                            </Text>
                        </Flex>
                        <Flex justify="space-between">
                            <Text fontSize="14px" color="neutral.500">
                                Tanggal/Waktu Upload
                            </Text>
                            <Text fontSize="14px" color="semantic.700">
                                {additionalInfo?.dateUpload}
                            </Text>
                        </Flex>
                        <Flex justify="space-between">
                            <Text fontSize="14px" color="neutral.500">
                                Tipe Sell Out
                            </Text>
                            <Text fontSize="14px" color="semantic.700">
                                {additionalInfo?.typeUpload}
                            </Text>
                        </Flex>
                        <Flex justify="space-between">
                            <Text fontSize="14px" color="neutral.500">
                                Bulan Sell Out
                            </Text>
                            <Text fontSize="14px" color="semantic.700">
                                {additionalInfo?.month}
                            </Text>
                        </Flex>
                    </VStack>
                </Flex>

                <Flex flexDirection="column" gap="16px" alignItems="center">
                    <ConditionalComponent statement={additionalInfo?.filePath}>
                        <Text fontSize="14px" textAlign="center" color="semantic.500">
                            Klik Download untuk melihat detail kesalahan di dalam file
                        </Text>
                    </ConditionalComponent>

                    <Flex gap="16px" justifyContent="space-between" alignItems="center" width="50%">
                        <Button variant="outline" width="100%" borderColor="sagitarius.500" onClick={onClose}>
                            Tutup
                        </Button>
                        <ConditionalComponent statement={additionalInfo?.filePath}>
                            <Button
                                variant="solid"
                                width="100%"
                                backgroundColor="sagitarius.500"
                                color="semantic.100"
                                isDisabled={!additionalInfo?.filePath}
                                onClick={() =>
                                    onDownloadFileError({
                                        path: additionalInfo?.filePath,
                                        onChangeDownloadNotification,
                                    })
                                }
                            >
                                Download
                            </Button>
                        </ConditionalComponent>
                    </Flex>

                    <Flex alignItems="center" gap="8px" cursor="pointer" color="semantic.500">
                        <QuestionBlueIcon />
                        <a href="https://s.id/info-sagita" target="_blank" rel="noopener noreferrer">
                            <Text fontSize="14px" fontWeight="600" textDecoration="underline">
                                Temukan Panduan Upload Disini!
                            </Text>
                        </a>
                    </Flex>
                </Flex>
            </ModalContent>
        </Modal>
    );
};

export default PopUp;
